import React, { useEffect, useState } from 'react';
import useAdminFetchApi from '../../utils/adminFetchApi';

export default function AdminBulkUpload() {
  const api = useAdminFetchApi();
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState('');
  const [csvFileId, setCsvFileId] = useState('');
  const [imagesFolderId, setImagesFolderId] = useState('');
  const [csvUrl, setCsvUrl] = useState('');
  const [imagesFolderUrl, setImagesFolderUrl] = useState('');
  const [fromRow, setFromRow] = useState('');
  const [toRow, setToRow] = useState('');
  const [start, setStart] = useState('');
  const [limit, setLimit] = useState('');
  const [concurrency, setConcurrency] = useState('3');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadChannels = async () => {
      try {
        const data = await api.get('/admin/channels');
        const list = Array.isArray(data) ? data : (data?.channels || []);
        if (!mounted) return;
        setChannels(list);
      } catch (e) {
        // leave empty silently; user can still type
      }
    };
    loadChannels();
    return () => { mounted = false; };
  }, [api]);

  const validate = () => {
    if (!channel) return 'Please select a channel';
    if (!csvFileId) return 'CSV File ID is required';
    if (!imagesFolderId) return 'Images Folder ID is required';
    if (concurrency !== '') {
      const n = Number(concurrency);
      if (!Number.isInteger(n) || n < 1) return 'Concurrency must be an integer >= 1';
    }
    if (fromRow !== '') {
      const n = Number(fromRow);
      if (!Number.isInteger(n) || n < 1) return 'From row must be an integer >= 1';
    }
    if (toRow !== '') {
      const n = Number(toRow);
      if (!Number.isInteger(n) || n < 1) return 'To row must be an integer >= 1';
    }
    if (fromRow !== '' && toRow !== '') {
      if (Number(toRow) < Number(fromRow)) return 'To row must be greater than or equal to From row';
    }
    // Only validate start/limit when fromRow/toRow are not provided (precedence rule)
    const hasRowRange = fromRow !== '' || toRow !== '';
    if (!hasRowRange) {
      if (start !== '') {
        const n = Number(start);
        if (!Number.isInteger(n) || n < 0) return 'Start must be an integer >= 0';
      }
      if (limit !== '') {
        const n = Number(limit);
        if (!Number.isInteger(n) || n < 1) return 'Limit must be an integer >= 1';
      }
    }
    return '';
  };

  const onUpload = async () => {
    setError('');
    setSuccess('');
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      // Build payload with precedence: fromRow/toRow override start/limit
      const payload = { channel, csvFileId, imagesFolderId };
      if (csvUrl) payload.csvUrl = csvUrl;
      if (imagesFolderUrl) payload.imagesFolderUrl = imagesFolderUrl;
      if (concurrency !== '') payload.concurrency = Number(concurrency);
      const hasRowRange = fromRow !== '' || toRow !== '';
      if (hasRowRange) {
        if (fromRow !== '') payload.fromRow = Number(fromRow);
        if (toRow !== '') payload.toRow = Number(toRow);
      } else {
        if (start !== '') payload.start = Number(start);
        if (limit !== '') payload.limit = Number(limit);
      }
      const resp = await api.post('/admin/bulk-upload', payload);
      setSuccess(resp?.message || 'Bulk upload started');
    } catch (e) {
      setError(e.message || 'Failed to start bulk upload');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="panel" style={{maxWidth:'840px'}}>
        <h3>Bulk Upload</h3>
        <div className="field">
          <label>Channel</label>
          <select value={channel} onChange={(e)=>setChannel(e.target.value)}>
            <option value="">Select a channel</option>
            {channels.map((c, idx) => {
              const value = c?.key || c?.id || c?.name || String(c);
              const label = c?.label || c?.name || c?.id || String(value);
              return <option key={value + '-' + idx} value={value}>{label}</option>;
            })}
          </select>
        </div>
        <div className="field">
          <label>CSV File ID</label>
          <input type="text" placeholder="Enter CSV File ID" value={csvFileId} onChange={(e)=>setCsvFileId(e.target.value)} />
        </div>
        <div className="field">
          <label>Images Folder ID</label>
          <input type="text" placeholder="Enter Images Folder ID" value={imagesFolderId} onChange={(e)=>setImagesFolderId(e.target.value)} />
        </div>
        <div className="field">
          <label>CSV URL (optional)</label>
          <input type="text" placeholder="https://.../file.csv" value={csvUrl} onChange={(e)=>setCsvUrl(e.target.value)} />
        </div>
        <div className="field">
          <label>Images Folder URL (optional)</label>
          <input type="text" placeholder="https://.../images/" value={imagesFolderUrl} onChange={(e)=>setImagesFolderUrl(e.target.value)} />
        </div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', gap: '12px'}}>
          <div className="field">
            <label>Concurrency (optional)</label>
            <input type="number" min={1} placeholder="e.g. 3" value={concurrency} onChange={(e)=>setConcurrency(e.target.value)} />
          </div>
          <div className="field">
            <label>From row (optional)</label>
            <input type="number" min={1} placeholder="1" value={fromRow} onChange={(e)=>setFromRow(e.target.value)} />
          </div>
          <div className="field">
            <label>To row (optional)</label>
            <input type="number" min={1} placeholder="e.g. 100" value={toRow} onChange={(e)=>setToRow(e.target.value)} />
          </div>
          <div className="field">
            <label>Start (optional)</label>
            <input type="number" min={0} placeholder="0" value={start} onChange={(e)=>setStart(e.target.value)} />
          </div>
          <div className="field">
            <label>Limit (optional)</label>
            <input type="number" min={1} placeholder="e.g. 100" value={limit} onChange={(e)=>setLimit(e.target.value)} />
          </div>
        </div>
        <div style={{fontSize:12, color:'#94a3b8', marginTop:4}}>
          Note: If From/To row are provided, Start/Limit will be ignored.
        </div>
        {error && <div className="error-message" style={{marginTop:12}}>{error}</div>}
        {success && <div className="error-message" style={{marginTop:12, background:'#133021', color:'#bbf7d0', borderColor:'#14532d'}}>{success}</div>}
        <div style={{marginTop:12, display:'flex', gap:8}}>
          <button className="button primary" onClick={onUpload} disabled={loading}>{loading ? 'Uploading…' : 'Upload'}</button>
        </div>
      </div>
    </div>
  );
}
